// Hideo - projects inside a profile: which one is active and what each one holds (logic without UI).
// The active project, or "All projects", is a device preference per profile: localStorage "creatorOS_project" holds
// { <profile id>: <project id> | 'all' } (stored format, never changed). The state lives in the projects store.
import { t, tn } from '../i18n'
import type { Creator, PedView, ProfileData, Project, Video } from '../data/types'
import { seriesOf } from './formats'
import { SERIES_COLORS, colorDot, safeColor } from '../utils/color'
import { esc } from '../utils/html'
import { videoScripts } from './scripts'
import { PROJECT_LISTS } from '../data/items'

export const PROJ_KEY = 'creatorOS_project'
/** Pseudo-project "All projects" (only with at least two projects) */
export const ALL_PROJECTS = 'all'
/** Items of the active project; with "All projects" the list itself */
export const ofProject = <T extends { projectId?: string }>(list: T[], active: string): T[] =>
  (active === ALL_PROJECTS ? list : list.filter(x => x.projectId === active))
/** Whether an item is shown with the active project (always with "All projects") */
export const inActive = (active: string, it: { projectId?: string }): boolean => active === ALL_PROJECTS || it.projectId === active

/** The stored choices of every profile ({} when missing or unreadable) */
export function parseActiveMap(raw: string | null): Record<string, string> {
  try {
    const m = JSON.parse(raw as string)
    return m && typeof m === 'object' && !Array.isArray(m) ? m : {}
  } catch {
    return {}
  }
}

/** Active project from the stored choice: the chosen one if it still exists, 'all' only with ≥ 2 projects,
 *  otherwise the first project ('all' when there is none) */
export function resolveActive(projects: Project[], choice: string | undefined): string {
  if (choice === ALL_PROJECTS && projects.length > 1) return ALL_PROJECTS
  return (projects.find(p => p.id === choice) || projects[0])?.id || ALL_PROJECTS
}

// ===== Project of a new item and project labels (ported from the legacy profile/projects.js; verbatim copy in
// tests/fixtures/legacy/projects.js. With the stores: newItemProject, projectTag in src/stores/projects.ts) =====
/** A project of the profile by id, or null */
export const projOf = (d: Pick<ProfileData, 'projects'>, id: string | undefined): Project | null => (d.projects || []).find(p => p.id === id) || null
/** The project chosen in a dialog if it still exists, otherwise the project of new items (fallback) */
export const validProject = (d: Pick<ProfileData, 'projects'>, pid: string | undefined, fallback: string): string =>
  (d.projects.some(p => p.id === pid) ? pid! : fallback)
/** Project of a new item: the active one; with "All projects" the first. With a saved plan open (Board and Calendar:
 *  `view`), the active project if it is in the plan, otherwise the plan's first */
export function newItemProjectOf(d: Pick<ProfileData, 'projects'>, active: string, view: PedView | null): string {
  if (view) return view.projects.includes(active) ? active : view.projects[0]
  return active !== ALL_PROJECTS ? active : (d.projects[0] || {} as Partial<Project>).id || ''
}
/** Coloured label of an item's project, only with "All projects" (or, on a Board card, a saved plan of several
 *  projects: `view`); '' otherwise or without a project; HTML (name escaped) */
export function projectTagHtml(d: Pick<ProfileData, 'projects'>, it: { projectId?: string } | null | undefined, active: string, view: PedView | null, cls = ''): string {
  if (active !== ALL_PROJECTS && !(view && view.projects.length > 1)) return ''
  const p = projOf(d, it ? it.projectId : undefined)
  if (!p) return ''
  return `<span class="${cls} inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border max-w-[10rem]" style="color:${safeColor(p.color)};border-color:${safeColor(p.color)}55;background:${safeColor(p.color)}14">${colorDot(p.color, 'w-1.5 h-1.5')}<span class="truncate">${esc(p.name)}</span></span>`
}

// Item counts shown for a project
const COUNTS: [('videos' | 'vault' | 'creators' | 'sources' | 'savedVideos'), (n: number) => string][] = [
  ['videos', n => tn(n, '{n} video', '{n} videos')],
  ['vault', n => tn(n, '{n} hook', '{n} hooks')],
  ['creators', n => tn(n, '{n} creator', '{n} creators')],
  ['sources', n => tn(n, '{n} source', '{n} sources')],
  ['savedVideos', n => tn(n, '{n} saved video', '{n} saved videos')]
]
/** "3 videos · 1 hook" of a project, "Empty" when it holds nothing */
export function projCounts(d: ProfileData, id: string): string {
  return COUNTS.map(([k, label]) => {
    const n = (d[k] as { projectId?: string }[]).filter(x => x.projectId === id).length
    return n ? label(n) : ''
  }).filter(Boolean).join(' · ') || t('Empty')
}

// ===== Copy to another project (the project picker of the video, hook, creator and source dialogs) =====
/** Item kinds of the dialogs' project picker: data list and id prefix of a copy */
export const COPY_KINDS = { video: ['videos', 'v_'], hook: ['vault', 'vt_'], creator: ['creators', 'c_'], source: ['sources', 's_'], saved: ['savedVideos', 'sv_'] } as const
export type CopyKind = keyof typeof COPY_KINDS

/** Copies a saved item into another project (a new id; the original is untouched) and returns the copy, or null when the
 *  item or the project does not exist. A video gets its own copies of its scripts, and the format with the same name in
 *  the target project (none if missing); the analysed videos of a creator get new ids. The caller saves. */
export function copyItem(d: ProfileData, kind: CopyKind, id: string, pid: string, now = Date.now()): { id: string; projectId?: string } | null {
  const [arr, pre] = COPY_KINDS[kind]
  const list = d[arr] as { id: string; projectId?: string }[]
  const src = list.find(x => x.id === id), p = d.projects.find(x => x.id === pid)
  if (!src || !p) return null
  const c = JSON.parse(JSON.stringify(src))
  c.id = pre + now; c.projectId = pid
  if (kind === 'video') {
    const v = src as Video
    // Each video has its own scripts: the copy gets new scripts with the same text
    c.scriptId = ''
    videoScripts(d, v).forEach((sc, i) => {
      const ns = JSON.parse(JSON.stringify(sc)); ns.id = 'sc_' + now + '_' + i; ns.updatedAt = now; ns.videoId = c.id; d.scripts.push(ns)
      if (sc.id === v.scriptId || !c.scriptId) c.scriptId = ns.id
    })
    // Formats are per project: the one with the same name in the target project is used, if any
    const s = seriesOf(d, v.seriesId), same = s && d.series.find(x => x.projectId === pid && x.name.toLowerCase() === s.name.toLowerCase())
    c.seriesId = same ? same.id : ''
  }
  if (kind === 'creator') ((c as Creator).analyzedVideos || []).forEach((a, i) => { a.id = 'av_' + now + '_' + i })
  list.push(c)
  return c
}

// ===== Manage projects (ProjectsDialog.vue) and the first project (FirstProjectDialog.vue) =====
/** Longest project name */
export const PROJECT_NAME_MAX = 40

/** Another project (not the one at index `except`) already has this name, ignoring case */
export const projectNameTaken = (d: ProfileData, name: string, except = -1): boolean =>
  d.projects.some((p, i) => i !== except && p.name.toLowerCase() === name.toLowerCase())

/** Colour proposed for a new project: the first of the palette no project uses yet */
export const freeProjectColor = (d: ProfileData): string =>
  SERIES_COLORS.find(c => !d.projects.some(p => p.color === c)) || SERIES_COLORS[0]

/** Number of items (videos, hooks, creators, sources, formats, keywords) in a project */
export const projectItemCount = (d: ProfileData, id: string): number =>
  PROJECT_LISTS.reduce((n, k) => n + (d[k] as { projectId?: string }[]).filter(x => x.projectId === id).length, 0)

/** Adds a project (name trimmed, at most 40 characters). 'empty' / 'taken' when the name is missing or already used.
 *  The caller saves. */
export function addProject(d: ProfileData, name: string, color: string, now = Date.now()): Project | 'empty' | 'taken' {
  const n = name.trim().slice(0, PROJECT_NAME_MAX)
  if (!n) return 'empty'
  if (projectNameTaken(d, n)) return 'taken'
  const p: Project = { id: 'pj_' + now, name: n, color: safeColor(color), descFooter: '' }
  d.projects.push(p)
  return p
}

/** Renames the project at index i. 'ok', or 'empty' / 'taken' (nothing changes); null when there is no such project.
 *  The caller saves. */
export function renameProject(d: ProfileData, i: number, name: string): 'ok' | 'empty' | 'taken' | null {
  const p = d.projects[i]
  if (!p) return null
  const n = String(name || '').trim().slice(0, PROJECT_NAME_MAX)
  if (!n) return 'empty'
  if (projectNameTaken(d, n, i)) return 'taken'
  p.name = n
  return 'ok'
}

/** Sets the colour of the project at index i (#rrggbb, otherwise the palette's first); false when there is no such
 *  project. The caller saves. */
export function setProjectColor(d: ProfileData, i: number, color: string): boolean {
  const p = d.projects[i]
  if (!p) return false
  p.color = safeColor(color)
  return true
}

/** Project that receives the content of a deleted one: the first other project (null when it is the only one) */
export const projectHeir = (d: ProfileData, id: string): Project | null =>
  d.projects.length > 1 ? d.projects.find(p => p.id !== id) || null : null

/** Deletes a project: its content moves to `heirId` (by default projectHeir), so no data is lost. At least one project
 *  must remain: false when it is the only one, or the project or the heir does not exist. The caller saves. */
export function deleteProject(d: ProfileData, id: string, heirId = projectHeir(d, id)?.id): boolean {
  const i = d.projects.findIndex(p => p.id === id)
  if (i === -1 || d.projects.length < 2 || !heirId || heirId === id || !d.projects.some(p => p.id === heirId)) return false
  PROJECT_LISTS.forEach(k => (d[k] as { projectId?: string }[]).forEach(x => { if (x.projectId === id) x.projectId = heirId }))
  d.projects.splice(i, 1)
  return true
}

/** Creates the first project of a profile without projects, and everything already there goes into it. Only once:
 *  with projects already there (Enter + click, double tap) nothing happens and it returns null; null with an empty name
 *  too. The caller saves. */
export function createFirstProject(d: ProfileData, name: string, color: string, now = Date.now()): Project | null {
  const n = name.trim().slice(0, PROJECT_NAME_MAX)
  if (!n || d.projects.length) return null
  const p: Project = { id: 'pj_' + now, name: n, color: safeColor(color), descFooter: '' }
  d.projects.push(p)
  PROJECT_LISTS.forEach(k => (d[k] as { projectId?: string }[]).forEach(x => { x.projectId = p.id }))
  return p
}
